import { useCallback, useMemo, useRef, useState } from "react";

import EventBusyIcon from "@mui/icons-material/EventBusy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { Desideratum, DesiderataKind, SlotTemplate } from "@/api/types";

const KIND_ICON: Record<DesiderataKind, React.ReactElement> = {
  WISH: <FavoriteIcon fontSize="small" />,
  AVOID: <ThumbDownAltOutlinedIcon fontSize="small" />,
  UNAVAILABLE: <EventBusyIcon fontSize="small" />,
  SWAP_OK: <SwapHorizIcon fontSize="small" />,
};

const KIND_COLOR: Record<DesiderataKind, string> = {
  WISH: "#2e7d32",
  AVOID: "#ed6c02",
  UNAVAILABLE: "#d32f2f",
  SWAP_OK: "#0288d1",
};

const KIND_BG: Record<DesiderataKind, string> = {
  WISH: "#e8f5e9",
  AVOID: "#fff3e0",
  UNAVAILABLE: "#ffebee",
  SWAP_OK: "#e1f5fe",
};

const KINDS: DesiderataKind[] = ["WISH", "AVOID", "UNAVAILABLE", "SWAP_OK"];

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}
function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

type CellKey = string; // "YYYY-MM-DD|slotId" or "YYYY-MM-DD|"

function cellKey(date: string, slotId: string | null): CellKey {
  return `${date}|${slotId ?? ""}`;
}
function parseCell(key: CellKey): { date: string; slotId: string | null } {
  const [date, slotIdRaw] = key.split("|");
  return { date: date as string, slotId: slotIdRaw ? slotIdRaw : null };
}

export type ApplyArgs = {
  cells: { date: string; slot_template_id: string | null }[];
  kind: DesiderataKind;
  weight: number;
};

export function DesiderataGrid({
  year,
  month,
  slots,
  desiderata,
  onApply,
  onClearCells,
  busy,
}: {
  year: number;
  month: number;
  slots: SlotTemplate[];
  desiderata: Desideratum[];
  onApply: (args: ApplyArgs) => void;
  onClearCells: (cells: { date: string; slot_template_id: string | null }[]) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const days = daysInMonth(year, month);

  // Build (date|slotId) → Desideratum map
  const desByKey = useMemo(() => {
    const m = new Map<CellKey, Desideratum>();
    for (const d of desiderata) {
      m.set(cellKey(d.date, d.slot_template_id ?? null), d);
    }
    return m;
  }, [desiderata]);

  const slotsColumns: Array<SlotTemplate | null> = [null, ...slots]; // null = whole day

  const [selection, setSelection] = useState<Set<CellKey>>(new Set());
  const dragRef = useRef<{ start: CellKey | null; baseline: Set<CellKey>; additive: boolean }>({
    start: null,
    baseline: new Set(),
    additive: false,
  });
  const [contextAnchor, setContextAnchor] = useState<{ x: number; y: number } | null>(null);

  const cellOrder = useMemo(() => {
    const out: CellKey[] = [];
    for (let d = 1; d <= days; d++) {
      const date = ymd(year, month, d);
      for (const s of slotsColumns) {
        out.push(cellKey(date, s?.id ?? null));
      }
    }
    return out;
  }, [days, year, month, slotsColumns]);

  const indexOf = useCallback(
    (k: CellKey) => cellOrder.indexOf(k),
    [cellOrder],
  );

  const rangeBetween = useCallback(
    (a: CellKey, b: CellKey): CellKey[] => {
      const ia = indexOf(a);
      const ib = indexOf(b);
      if (ia < 0 || ib < 0) return [];
      const [lo, hi] = ia < ib ? [ia, ib] : [ib, ia];
      return cellOrder.slice(lo, hi + 1);
    },
    [cellOrder, indexOf],
  );

  const handleMouseDown = (e: React.MouseEvent, key: CellKey) => {
    if (e.button !== 0) return;
    const additive = e.ctrlKey || e.metaKey;
    const range = e.shiftKey && dragRef.current.start;
    if (range) {
      const next = new Set(additive ? selection : new Set<CellKey>());
      for (const k of rangeBetween(dragRef.current.start as CellKey, key)) {
        next.add(k);
      }
      setSelection(next);
      dragRef.current = { start: key, baseline: next, additive };
    } else {
      dragRef.current = {
        start: key,
        baseline: additive ? new Set(selection) : new Set(),
        additive,
      };
      const next = additive ? new Set(selection) : new Set<CellKey>();
      next.add(key);
      setSelection(next);
    }
    e.preventDefault();
  };

  const handleMouseEnter = (e: React.MouseEvent, key: CellKey) => {
    if (e.buttons !== 1 || !dragRef.current.start) return;
    const next = new Set(dragRef.current.baseline);
    for (const k of rangeBetween(dragRef.current.start, key)) next.add(k);
    setSelection(next);
  };

  const handleContextMenu = (e: React.MouseEvent, key: CellKey) => {
    e.preventDefault();
    if (!selection.has(key)) {
      // Right-click on an unselected cell → select just it
      const next = new Set<CellKey>();
      next.add(key);
      setSelection(next);
    }
    setContextAnchor({ x: e.clientX + 2, y: e.clientY - 6 });
  };

  const clearSelection = () => setSelection(new Set());

  const handleMenuPick = (kind: DesiderataKind, weight: number) => {
    setContextAnchor(null);
    const arr = Array.from(selection).map(parseCell);
    onApply({
      cells: arr.map((c) => ({ date: c.date, slot_template_id: c.slotId })),
      kind,
      weight,
    });
  };

  const handleMenuClear = () => {
    setContextAnchor(null);
    const arr = Array.from(selection)
      .map(parseCell)
      .filter((c) => desByKey.has(cellKey(c.date, c.slotId)));
    onClearCells(
      arr.map((c) => ({ date: c.date, slot_template_id: c.slotId })),
    );
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap" }}
      >
        <Alert severity="info" sx={{ flex: 1, py: 0 }}>
          {t("desiderata.gridHelp")}
        </Alert>
        {selection.size > 0 && (
          <>
            <Chip
              color="primary"
              label={t("desiderata.selectionCount", { count: selection.size })}
            />
            <Button size="small" onClick={clearSelection}>
              {t("desiderata.clearSelection")}
            </Button>
          </>
        )}
      </Stack>

      <Box
        sx={{
          overflowX: "auto",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          userSelect: "none",
        }}
        onMouseLeave={() => {
          dragRef.current.start = null;
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `100px repeat(${slotsColumns.length}, minmax(80px, 1fr))`,
            minWidth: `${100 + slotsColumns.length * 80}px`,
          }}
        >
          {/* Header row */}
          <Box sx={hcellSx}>{t("desiderata.day")}</Box>
          {slotsColumns.map((s, i) => (
            <Box key={i} sx={{ ...hcellSx, textAlign: "center" }}>
              {s ? (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" component="div" color="text.secondary">
                    {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t("desiderata.wholeDay")}
                </Typography>
              )}
            </Box>
          ))}

          {/* Day rows */}
          {Array.from({ length: days }, (_, i) => i + 1).map((day) => {
            const date = ymd(year, month, day);
            const dow = new Date(year, month - 1, day).getDay();
            const isWeekend = dow === 0 || dow === 6;
            return (
              <Box
                key={day}
                sx={{ display: "contents" }}
                role="row"
              >
                <Box
                  sx={{
                    ...hcellSx,
                    bgcolor: isWeekend ? "action.hover" : "background.default",
                    fontWeight: 600,
                  }}
                >
                  {pad2(day)}
                </Box>
                {slotsColumns.map((s, ci) => {
                  const k = cellKey(date, s?.id ?? null);
                  const d = desByKey.get(k);
                  const selected = selection.has(k);
                  return (
                    <Box
                      key={ci}
                      role="gridcell"
                      onMouseDown={(e) => handleMouseDown(e, k)}
                      onMouseEnter={(e) => handleMouseEnter(e, k)}
                      onContextMenu={(e) => handleContextMenu(e, k)}
                      sx={{
                        ...cellSx,
                        bgcolor: selected
                          ? "primary.light"
                          : d
                            ? KIND_BG[d.kind]
                            : isWeekend
                              ? "action.hover"
                              : "background.paper",
                        outline: selected ? "2px solid" : "none",
                        outlineColor: "primary.main",
                        outlineOffset: -2,
                        cursor: "pointer",
                      }}
                    >
                      {d && (
                        <Tooltip
                          title={`${d.kind} · ${d.weight}pt${d.comment ? ` · ${d.comment}` : ""}`}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ color: KIND_COLOR[d.kind] }}>{KIND_ICON[d.kind]}</Box>
                            {d.kind !== "UNAVAILABLE" && (
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {d.weight}
                              </Typography>
                            )}
                          </Stack>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Menu
        open={contextAnchor !== null}
        onClose={() => setContextAnchor(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextAnchor !== null ? { top: contextAnchor.y, left: contextAnchor.x } : undefined
        }
      >
        <MenuItem disabled>
          <Typography variant="caption">
            {t("desiderata.selectionCount", { count: selection.size })}
          </Typography>
        </MenuItem>
        <Divider />
        {KINDS.map((k) => (
          <MenuItem
            key={k}
            disabled={busy}
            onClick={() =>
              handleMenuPick(k, k === "UNAVAILABLE" ? 1 : 3)
            }
          >
            <ListItemIcon sx={{ color: KIND_COLOR[k] }}>{KIND_ICON[k]}</ListItemIcon>
            <ListItemText>
              {t(`desiderata.kind.${k}`)}
              {k !== "UNAVAILABLE" ? ` (3pt)` : ""}
            </ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          disabled={busy}
          onClick={handleMenuClear}
        >
          <ListItemText>{t("desiderata.clearChoice")}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}


const hcellSx = {
  px: 1,
  py: 0.75,
  borderBottom: 1,
  borderRight: 1,
  borderColor: "divider",
  fontSize: 12,
  bgcolor: "background.default",
} as const;

const cellSx = {
  minHeight: 36,
  px: 0.5,
  py: 0.5,
  borderBottom: 1,
  borderRight: 1,
  borderColor: "divider",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 80ms",
} as const;
